import { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  checkoutFormSchema,
  type CheckoutFormData,
} from "@/src/lib/schemas/checkout-schemas";
import { useCheckout } from "@tagadapay/plugin-sdk/v2";

export function useCheckoutForm({ checkoutToken }: { checkoutToken: string }) {
  const { checkout, updateCustomerAndSessionInfo } = useCheckout({
    checkoutToken,
  });

  const form = useForm<CheckoutFormData>({
    mode: "all",
    resolver: zodResolver(checkoutFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      postal: "",
      country: "",
      sameBillingAddress: true,
      billingFirstName: "",
      billingLastName: "",
      billingAddress: "",
      billingCountry: "",
      billingState: "",
      billingCity: "",
      billingZip: "",
      paymentMethod: "card",
      cardNumber: "",
      expiryDate: "",
      cvc: "",
    },
  });

  const hasInitializedForm = useRef(false);

  // Initialize form from checkout session
  useEffect(() => {
    if (checkout?.checkoutSession && !hasInitializedForm.current) {
      const session = checkout.checkoutSession;
      const customer = session.customer;
      const shipping = session.shippingAddress;
      const billing = session.billingAddress;
      const differentBilling = session.differentBillingAddress;

      form.setValue("firstName", customer?.firstName || "");
      form.setValue("lastName", customer?.lastName || "");
      form.setValue("email", customer?.email || "");
      form.setValue("phone", shipping?.phone || "");
      form.setValue("country", shipping?.country || "");
      form.setValue("address1", shipping?.address1 || "");
      form.setValue("address2", shipping?.address2 || "");
      form.setValue("city", shipping?.city || "");
      form.setValue("state", shipping?.state || shipping?.province || "");
      form.setValue("postal", shipping?.postal || "");

      // Initialize billing address fields
      if (differentBilling && billing) {
        form.setValue("sameBillingAddress", false);
        form.setValue("billingFirstName", billing.firstName || "");
        form.setValue("billingLastName", billing.lastName || "");
        form.setValue("billingAddress", billing.address1 || "");
        form.setValue("billingCountry", billing.country || "");
        form.setValue("billingState", billing.state || billing.province || "");
        form.setValue("billingCity", billing.city || "");
        form.setValue("billingZip", billing.postal || "");
      } else {
        form.setValue("sameBillingAddress", true);
      }

      hasInitializedForm.current = true;
    }
  }, [checkout?.checkoutSession, form]);

  // Auto-save form data
  const saveCheckoutInfo = useCallback(
    async (addressData: CheckoutFormData) => {
      console.log("addressData", addressData);
      if (!checkout?.checkoutSession?.id) {
        return;
      }

      if (!addressData) {
        return;
      }

      try {
        const enhancedAddressData = {
          ...addressData,
          state:
            addressData.state && addressData.state.trim()
              ? addressData.state.trim()
              : "N/A",
        };

        if (
          !enhancedAddressData.email &&
          !enhancedAddressData.firstName &&
          !enhancedAddressData.address1 &&
          !enhancedAddressData.country
        ) {
          return;
        }

        const shippingAddress = {
          firstName: enhancedAddressData.firstName,
          lastName: enhancedAddressData.lastName,
          address1: enhancedAddressData.address1,
          address2: enhancedAddressData.address2 || "",
          city: enhancedAddressData.city,
          country: enhancedAddressData.country,
          state: enhancedAddressData.state,
          postal: enhancedAddressData.postal,
          phone: enhancedAddressData.phone,
        };

        // Determine if billing address is different
        const differentBillingAddress = !enhancedAddressData.sameBillingAddress;

        // Build billing address based on sameBillingAddress flag
        const billingAddress = differentBillingAddress
          ? {
              firstName: enhancedAddressData.billingFirstName || "",
              lastName: enhancedAddressData.billingLastName || "",
              address1: enhancedAddressData.billingAddress || "",
              address2: "",
              city: enhancedAddressData.billingCity || "",
              country: enhancedAddressData.billingCountry || "",
              state:
                enhancedAddressData.billingState &&
                enhancedAddressData.billingState.trim()
                  ? enhancedAddressData.billingState.trim()
                  : "N/A",
              postal: enhancedAddressData.billingZip || "",
              phone: enhancedAddressData.phone, // Reuse shipping phone for billing
            }
          : shippingAddress;

        await updateCustomerAndSessionInfo({
          customerData: {
            email: enhancedAddressData.email,
            acceptsMarketing: false,
          },
          shippingAddress,
          billingAddress,
          differentBillingAddress,
        });
      } catch (error) {
        console.error("❌ Failed to auto-save checkout info:", error);
      }
    },
    [checkout?.checkoutSession?.id, updateCustomerAndSessionInfo],
  );

  // Debounced save function
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedSaveCheckoutInfo = useCallback(
    (value: CheckoutFormData) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        console.log("value4", timeoutRef.current);
        saveCheckoutInfo(value);
      }, 200);
    },
    [saveCheckoutInfo],
  );

  // Flush any pending debounced saves and save immediately
  const flushAndSaveCheckoutInfo = useCallback(
    async (value?: CheckoutFormData) => {
      // Clear any pending debounced save
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Save immediately with current form values or provided value
      const dataToSave = value || form.getValues();
      await saveCheckoutInfo(dataToSave);
    },
    [form, saveCheckoutInfo],
  );

  // Auto-save on field changes (skip during initial form load)
  useEffect(() => {
    if (!hasInitializedForm.current) {
      return;
    }

    const subscription = form.watch((value: any) => {
      // Only save if form has been initialized and has required fields
      // Check for address fields specifically to ensure address data is saved
      const hasAddressData =
        value.address1 && value.country && value.city && value.postal;
      const hasCustomerData = value.firstName || value.lastName || value.email;

      if (hasInitializedForm.current && (hasAddressData || hasCustomerData)) {
        /*  console.log(
          "[useCheckoutForm] Customer info changed, syncing to SDK",
          value,
        ); */
        debouncedSaveCheckoutInfo(value as CheckoutFormData);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [form, debouncedSaveCheckoutInfo]);

  return {
    form,
    saveCheckoutInfo,
    flushAndSaveCheckoutInfo,
  };
}
